/**
 * Opening hours of a location (`appointmentTimeslotConfig`) as the Wunschzeiten windows patients choose from. Hours
 * are numbers in half-hour steps (7.5 is 7:30). The module imports nothing, so `npm run test:unit` runs it under plain
 * Node. Kept identical in physiofit_practice_dashboard and physiofit_patient_webapp, together with its test; change
 * both copies at once.
 */

export type TimeWindow = { startHour: number; endHour: number };

const MINUTES_PER_HOUR = 60;
const LATEST_HOUR = 23;

export const normalizeOpeningHour = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.min(LATEST_HOUR, Math.max(0, Math.round(value * 2) / 2));
};

const roundToFullHour = (minutes: number) => {
  const pastFullHour = minutes % MINUTES_PER_HOUR;
  const fullHour = minutes - pastFullHour;
  return pastFullHour <= MINUTES_PER_HOUR / 2 ? fullHour : fullHour + MINUTES_PER_HOUR;
};

/**
 * Up to three windows: the inner boundaries sit at one and two thirds of the day, rounded to the full hour (exactly
 * half past rounds down), while start and end stay as the practice set them. A window the rounding leaves empty is
 * dropped, so a short day gets fewer than three.
 */
export const deriveTimeWindows = (startHour: number, endHour: number): TimeWindow[] => {
  const start = startHour * MINUTES_PER_HOUR;
  const end = endHour * MINUTES_PER_HOUR;
  const third = (end - start) / 3;
  const boundaries = [start, roundToFullHour(start + third), roundToFullHour(start + 2 * third), end];

  return boundaries
    .slice(0, -1)
    .map((boundary, index) => ({
      startHour: boundary / MINUTES_PER_HOUR,
      endHour: boundaries[index + 1] / MINUTES_PER_HOUR,
    }))
    .filter((window) => window.endHour > window.startHour);
};

const formatWindowTime = (hour: number) => {
  const totalMinutes = Math.round(hour * MINUTES_PER_HOUR);
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return minutes === 0 ? `${hours}` : `${hours}:${minutes.toString().padStart(2, '0')}`;
};

/** "7:30 - 11": minutes only where they are not ":00", so three windows fit one line on a phone. */
export const formatWindowRange = (startHour: number, endHour: number) =>
  `${formatWindowTime(startHour)} - ${formatWindowTime(endHour)}`;
